import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Star } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useVatRate,
  useUpdateVatRate,
  useWithholdingRates,
  useCreateWithholdingRate,
  useUpdateWithholdingRate,
  useDeleteWithholdingRate,
} from '@/hooks/finance/useTaxSettings';
import type { WithholdingTaxRate, CreateWithholdingRateInput } from '@/types/database/tax-settings';

export default function TaxSettingsPage() {
  const navigate = useNavigate();
  
  // VAT state
  const { data: vatRate, isLoading: vatLoading } = useVatRate();
  const updateVatRate = useUpdateVatRate();
  const [editVatRate, setEditVatRate] = useState<string>('');
  const [isEditingVat, setIsEditingVat] = useState(false);
  
  // Withholding rates state
  const { data: withholdingRates, isLoading: ratesLoading } = useWithholdingRates();
  const createRate = useCreateWithholdingRate();
  const updateRate = useUpdateWithholdingRate();
  const deleteRate = useDeleteWithholdingRate();
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<WithholdingTaxRate | null>(null);
  const [formData, setFormData] = useState<CreateWithholdingRateInput>({
    name: '',
    rate: 0,
    description: '',
    is_default: false,
    is_active: true,
    sort_order: 0,
  });

  const handleStartEditVat = () => {
    setEditVatRate(String(vatRate || 15));
    setIsEditingVat(true);
  };

  const handleSaveVat = () => {
    const rate = parseFloat(editVatRate);
    if (!isNaN(rate) && rate >= 0 && rate <= 100) {
      updateVatRate.mutate(rate, {
        onSuccess: () => setIsEditingVat(false),
      });
    }
  };

  const handleOpenDialog = (rate?: WithholdingTaxRate) => {
    if (rate) {
      setSelectedRate(rate);
      setFormData({
        name: rate.name,
        rate: rate.rate,
        description: rate.description || '',
        is_default: rate.is_default,
        is_active: rate.is_active,
        sort_order: rate.sort_order,
      });
    } else {
      setSelectedRate(null);
      setFormData({
        name: '',
        rate: 0,
        description: '',
        is_default: false,
        is_active: true,
        sort_order: (withholdingRates?.length || 0) + 1,
      });
    }
    setDialogOpen(true);
  };

  const handleSaveRate = () => {
    if (selectedRate) {
      updateRate.mutate(
        { id: selectedRate.id, ...formData },
        { onSuccess: () => setDialogOpen(false) }
      );
    } else {
      createRate.mutate(formData, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const handleDeleteRate = () => {
    if (selectedRate) {
      deleteRate.mutate(selectedRate.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedRate(null);
        },
      });
    }
  };

  const openDeleteDialog = (rate: WithholdingTaxRate) => {
    setSelectedRate(rate);
    setDeleteDialogOpen(true);
  };

  return (
    <Layout>
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/finance')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Tax Settings</h1>
          <p className="text-muted-foreground">Configure VAT and withholding tax rates</p>
        </div>
      </div>

      {/* VAT Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>VAT Configuration</CardTitle>
          <CardDescription>
            Set the default VAT rate applied when VAT is enabled on expenses and payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="vat-rate">Default VAT Rate (%)</Label>
              {isEditingVat ? (
                <Input
                  id="vat-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editVatRate}
                  onChange={(e) => setEditVatRate(e.target.value)}
                  className="mt-1.5"
                />
              ) : (
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-2xl font-semibold">{vatLoading ? '...' : vatRate}%</span>
                </div>
              )}
            </div>
            {isEditingVat ? (
              <div className="flex gap-2">
                <Button onClick={handleSaveVat} disabled={updateVatRate.isPending}>
                  {updateVatRate.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setIsEditingVat(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={handleStartEditVat}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Withholding Tax Rates */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Withholding Tax Rates</CardTitle>
            <CardDescription>
              Manage available withholding tax rates for expenses and payments
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Rate
          </Button>
        </CardHeader>
        <CardContent>
          {ratesLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : withholdingRates && withholdingRates.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {withholdingRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell className="font-medium">{rate.name}</TableCell>
                    <TableCell>{rate.rate}%</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {rate.description || '-'}
                    </TableCell>
                    <TableCell>
                      {rate.is_default && (
                        <Star className="h-4 w-4 text-primary fill-primary" />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={rate.is_active ? 'text-primary' : 'text-muted-foreground'}>
                        {rate.is_active ? 'Yes' : 'No'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(rate)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(rate)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No withholding tax rates configured. Click "Add Rate" to create one.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedRate ? 'Edit Withholding Rate' : 'Add Withholding Rate'}
            </DialogTitle>
            <DialogDescription>
              {selectedRate
                ? 'Update the withholding tax rate details'
                : 'Create a new withholding tax rate option'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Services"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate">Rate (%)</Label>
              <Input
                id="rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="When to use this rate..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_default">Set as default</Label>
              <Switch
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Active</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRate}
              disabled={!formData.name || createRate.isPending || updateRate.isPending}
            >
              {createRate.isPending || updateRate.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Withholding Rate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRate}>
              {deleteRate.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </Layout>
  );
}
